import { StrictMode, Suspense, lazy, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  isRouteErrorResponse,
  Outlet,
  RouterProvider,
  useRouteError,
} from "react-router-dom";
import { AuthenticatedLayout } from "./app/AuthenticatedLayout.js";
import { AppProviders } from "./app/providers.js";
import { Button, Skeleton } from "./components/ui/index.js";
import {
  AcceptInvitePage,
  HomeRedirect,
  LoginPage,
  SetupPage,
} from "./features/auth/pages.js";
import DashboardPage from "./features/dashboard.js";
import { useI18n } from "./i18n/index.js";
import { registerChunkRecovery } from "./lib/chunk-recovery.js";
import { isReloadGuarded } from "./lib/reload-guard.js";
import { initializeTheme } from "./theme/index.js";
import { PluginPageHost } from "./plugins/PluginPageHost.js";
import { synchronizeBrowserNavigation } from "./plugins/browser-navigation.js";
import { PublicPluginPageHost } from "./plugins/PublicPluginPageHost.js";
import "./styles/globals.css";

const UsersPage = lazy(() => import("./features/users/UsersPage.js"));
const GroupsPage = lazy(() => import("./features/groups/GroupsPage.js"));
const ApiKeysPage = lazy(() => import("./features/api-keys/ApiKeysPage.js"));
const WebhooksPage = lazy(() => import("./features/webhooks/WebhooksPage.js"));
const PluginsPage = lazy(() => import("./features/plugins/PluginsPage.js"));
const AuditPage = lazy(() => import("./features/audit/AuditPage.js"));
const GeneralSettingsPage = lazy(
  () => import("./features/settings/GeneralSettingsPage.js"),
);

registerChunkRecovery();
initializeTheme();
const browserNavigation = synchronizeBrowserNavigation();
if (import.meta.hot) import.meta.hot.dispose(browserNavigation.dispose);

const pending = (
  <div className="space-y-4">
    <Skeleton className="h-12" />
    <Skeleton className="h-72" />
  </div>
);
const lazyElement = (Component: ComponentType) => (
  <Suspense fallback={pending}>
    <Component />
  </Suspense>
);

const RouteErrorPage = () => {
  const { t } = useI18n();
  const error = useRouteError();
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : t("errors.pageLoadDescription");

  return (
    <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-lg rounded-2xl border bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-bold text-slate-900">
          {t("errors.pageLoadTitle")}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {t("errors.pageLoadDescription")}
        </p>
        <p className="mt-3 break-words rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
          {detail}
        </p>
        <Button className="mt-5" onClick={() => window.location.reload()}>
          {t("errors.reloadPage")}
        </Button>
      </div>
    </main>
  );
};

const AuthenticatedContent = () => <Outlet />;

const AuthenticatedRouteErrorPage = () => {
  const { t } = useI18n();
  const error = useRouteError();
  const detail =
    error instanceof Error ? error.message : t("errors.pageLoadDescription");
  const reload = () => {
    if (isReloadGuarded()) {
      window.dispatchEvent(new CustomEvent("app:request-safe-reload"));
      return;
    }
    window.location.reload();
  };
  return (
    <div className="mx-auto max-w-xl rounded-2xl border bg-white p-8 text-center shadow-sm">
      <h1 className="text-xl font-bold">{t("errors.pageLoadTitle")}</h1>
      <p className="mt-2 text-sm text-slate-500">{detail}</p>
      <div className="mt-5 flex justify-center gap-3">
        <Button variant="secondary" onClick={() => window.history.back()}>
          {t("common.back")}
        </Button>
        <Button onClick={reload}>
          {isReloadGuarded()
            ? t("errors.reloadGuarded")
            : t("errors.reloadPage")}
        </Button>
      </div>
    </div>
  );
};

const router = createBrowserRouter(
  [
    { path: "/", element: <HomeRedirect /> },
    { path: "/setup", element: <SetupPage /> },
    { path: "/login", element: <LoginPage /> },
    { path: "/accept-invite", element: <AcceptInvitePage /> },
    {
      path: "/app",
      element: <AuthenticatedLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        {
          element: <AuthenticatedContent />,
          errorElement: <AuthenticatedRouteErrorPage />,
          children: [
            { index: true, element: <DashboardPage /> },
            { path: "users", element: lazyElement(UsersPage) },
            { path: "groups", element: lazyElement(GroupsPage) },
            { path: "settings/api-keys", element: lazyElement(ApiKeysPage) },
            { path: "settings/webhooks", element: lazyElement(WebhooksPage) },
            { path: "plugins/:tab?", element: lazyElement(PluginsPage) },
            { path: "audit", element: lazyElement(AuditPage) },
            { path: "p/:pluginId/*", element: <PluginPageHost /> },
            {
              path: "settings/general",
              element: lazyElement(GeneralSettingsPage),
            },
          ],
        },
      ],
    },
    { path: "*", element: <PublicPluginPageHost /> },
  ],
  { window: browserNavigation.coreWindow },
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);
